import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Menu, MenuItem } from "@mui/material";
import { useId, useState } from "react";
import { COPY } from "@/constants/campaignContacts";

interface AddRecipientsMenuProps {
  /** The first option's second line: price, free spaces, or "Draft — no charge". */
  subtitleOne: string;
  onAddOne: () => void;
  /** The container decides where bulk goes (the importer on top, or the builder for a draft). */
  onBulk: () => void;
}

const triggerSx = {
  px: 2.5,
  py: 1.25,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  borderRadius: "var(--radius-btn)",
  bgcolor: "var(--color-good-ochre)",
  color: "#fff",
  "&:hover": { bgcolor: "var(--color-rodeo-black)" },
} as const;

const itemSx = {
  display: "block",
  maxWidth: 320,
  whiteSpace: "normal",
  px: 2,
  py: 1.25,
} as const;

function MenuOption({ label, sub }: { label: string; sub: string }) {
  return (
    <>
      <span className="block text-[14px] font-bold text-rodeo-black">{label}</span>
      <span className="block text-[12px] text-graphite">{sub}</span>
    </>
  );
}

/** "Add recipients" dropdown: one recipient by form, or a spreadsheet through the importer. */
export default function AddRecipientsMenu({
  subtitleOne,
  onAddOne,
  onBulk,
}: AddRecipientsMenuProps) {
  const T = COPY.addMenu;
  const id = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  function pick(action: () => void) {
    setAnchor(null);
    action();
  }

  return (
    <>
      <Button
        variant="contained"
        disableElevation
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-controls={anchor !== null ? `${id}-menu` : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<AddIcon sx={{ fontSize: 15 }} />}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={triggerSx}
      >
        {T.button}
      </Button>
      <Menu
        id={`${id}-menu`}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem onClick={() => pick(onAddOne)} sx={itemSx}>
          <MenuOption label={T.one} sub={subtitleOne} />
        </MenuItem>
        <MenuItem onClick={() => pick(onBulk)} sx={itemSx}>
          <MenuOption label={T.bulk} sub={T.bulkSub} />
        </MenuItem>
      </Menu>
    </>
  );
}
